import React, { useEffect, useRef } from 'react';

/**
 * Text edited in place, inside the step row.
 *
 * Renders as plain prose until focused, so a row still reads as a sentence
 * rather than a form. It grows with its content instead of scrolling, because a
 * step's text is usually one line and a fixed-height box would waste the row.
 */
export default function InlineText({
    value,
    onChange,
    placeholder,
    multiline = false,
    className = '',
    onEnter,
    ariaLabel,
}) {
    const ref = useRef(null);

    // Keep the height matched to the content on every change, including the
    // first paint of a saved value.
    useEffect(() => {
        const el = ref.current;
        if (!el || !multiline) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [value, multiline]);

    const shared = {
        ref,
        'aria-label': ariaLabel,
        value: value || '',
        placeholder,
        onChange: e => onChange(e.target.value),
        // The row itself toggles selection on click; a click meant for the
        // caret must not bubble up and collapse the row.
        onClick: e => e.stopPropagation(),
        onMouseDown: e => e.stopPropagation(),
        className: `w-full min-w-0 resize-none border-0 bg-transparent p-0 text-[14px] leading-6
            text-gray-800 placeholder:text-gray-400 focus:ring-0 ${className}`,
    };

    if (multiline) {
        return <textarea {...shared} rows={1} />;
    }

    return (
        <input
            {...shared}
            type="text"
            onKeyDown={e => {
                if (e.key === 'Enter' && onEnter) {
                    e.preventDefault();
                    onEnter();
                }
            }}
        />
    );
}
